import { useContext, useEffect, useState } from 'react';
import { Card, CardDataType, MobileCard } from '@unified-checkout/card';
import {
  Accordion,
  FeaturedIcon,
  Notification,
} from '@unified-checkout/design-system';
import { CreditCard, User } from '@unified-checkout/design-system/icons/index';
import { getCardLogo, useHookForm } from '@unified-checkout/kit-components';
import { useAddCard } from '../../../../services/inner_layout/paymentMethods';
import { methods } from '../../../../constants';
import { DeviceContext } from '../../../../context/device';
import { useLocation, useNavigate } from 'react-router-dom';

interface HandleSubmitProps {
  data?: CardDataType;
  isRetrying?: boolean;
}

export const AddPaymentMethod = ({ show }: { show: boolean | undefined }) => {
  const { isMobile } = useContext(DeviceContext);
  const [cardStatus, setCardStatus] = useState<'idle' | 'success' | 'error'>(
    'idle'
  );
  const location = useLocation();
  const navigate = useNavigate();
  const query = location.state?.query;

  const handleSubmit = ({ data, isRetrying }: HandleSubmitProps) => {
    if (isRetrying && variables) {
      return addPaymentMutation({ ...variables });
    }
    if (data) {
      const { cvc, expiry, name, number } = data;
      const expiryArr = expiry.split('/');
      addPaymentMutation({
        cvn: cvc,
        expiry_month: expiryArr[0],
        expiry_year: expiryArr[1],
        identifier: number.replace(/\s/g, ''),
        sourceholder_name: name,
        subtype: 'CARD',
      });
    }
  };

  const {
    mutate: addPaymentMutation,
    isPending: isPendingAddingPaymentMethod,
    isSuccess: isSuccessAddingPaymentMethod,
    isError: isErrorAddingPaymentMethod,
    reset,
    variables,
  } = useAddCard();

  useEffect(() => {
    if (!query) return;
    const params = new URLSearchParams(query);
    const isSuccessValue = params.get('success');
    if (isSuccessValue === 'true') {
      setCardStatus('success');
    } else if (isSuccessValue === 'false') {
      setCardStatus('error');
    }
    navigate(location.pathname, { replace: true, state: {} });
  }, [query]);

  const accordionHeader = (
    <>
      <div className="flex items-center justify-center w-10 h-10 rounded-md md:w-12 md:h-12 shrink-0">
        <FeaturedIcon
          size={isMobile ? 'md' : 'lg'}
          color="primary"
          icon={CreditCard}
        />
      </div>
      <p className="text-gray-900 english-body-md-semibold">Card</p>
    </>
  );
  const handleCloseNotification = () => {
    reset();
    setCardStatus('idle');
  };

  return (
    <div className="md:bg-white relative !mb-5">
      {(cardStatus !== 'idle' || isErrorAddingPaymentMethod) && (
        <div className="fixed bottom-[1.875rem] left-4 right-4 md:left-[calc(18rem+2.9%)] md:right-[2.9%] z-10">
          {cardStatus === 'success' && (
            <Notification
              show={cardStatus === 'success'}
              title="Card added successfully"
              icon={CreditCard}
              onCloseHandler={handleCloseNotification}
              state="primary"
              isMobile={isMobile}
              timeout={3}
            />
          )}
          {(isErrorAddingPaymentMethod || cardStatus === 'error') && (
            <Notification
              show={isErrorAddingPaymentMethod || cardStatus === 'error'}
              title="Card couldn’t be added"
              icon={CreditCard}
              onCloseHandler={handleCloseNotification}
              state="danger"
              type="alert"
              hintText="There was some technical issue"
              btnsProps={
                isErrorAddingPaymentMethod
                  ? [
                      {
                        text: 'Try again',
                        variant: 'link',
                        onClickHandler: () =>
                          handleSubmit({ isRetrying: true }),
                      },
                    ]
                  : undefined
              }
              isMobile={isMobile}
              hasCloseIcon
            />
          )}
        </div>
      )}
      {show ? (
        <Accordion
          isOpen
          hasDivider
          header={accordionHeader}
          alignment="center"
        >
          {isMobile ? (
            <MobileCard
              hideCardIcons
              onSubmit={(data: CardDataType) => handleSubmit({ data })}
              loading={isPendingAddingPaymentMethod}
              getCardLogo={() => undefined}
              showSaveCard={false}
              useHookForm={useHookForm}
              showTerms={false}
              noCardLogo
              icon={User}
              content={
                <div className="flex items-center gap-2 mt-3">
                  <p className="text-gray-600 english-utility-helper-text-regular">
                    We accept
                  </p>
                  <ul className="flex [&_svg]:w-7 [&_svg]:h-5 gap-1">
                    {methods.map((method, index) => (
                      <li key={index}>{method.icon}</li>
                    ))}
                  </ul>
                </div>
              }
              buttonLabel="Save card"
              isDisabled={isPendingAddingPaymentMethod}
            />
          ) : (
            <Card
              onSubmit={(data: CardDataType) => handleSubmit({ data })}
              loading={isPendingAddingPaymentMethod}
              getCardLogo={getCardLogo}
              showSaveCard={false}
              useHookForm={useHookForm}
              showTerms={false}
              buttonLabel="Save card"
            />
          )}
        </Accordion>
      ) : null}
    </div>
  );
};
